import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getUpcomingEventSummaries,
  type UpcomingEventSummaryResult,
  type UpcomingEventSummaryRow,
} from "../../services/eventExecutiveSummary.service";

// Owner 2026-10-02: the team wants to see the weekend's events before they start. The main
// Event Performance table lists only events whose local day has arrived; this section lists the
// rest. The server scope is the exact complement, so an event moves from here to the main table at
// local midnight of its start day (migration 2026-10-02-100).

const DAY_OPTIONS = [7, 14, 30] as const;
const count = new Intl.NumberFormat("en-US");

/** Start date/time in the EVENT's own timezone, e.g. "Sat, Oct 3 · 9:00 AM EDT". */
export const formatEventStart = (row: UpcomingEventSummaryRow): string => {
  const tz = row.event_timezone?.trim() || "America/Chicago";
  if (row.event_start_at) {
    const d = new Date(row.event_start_at);
    if (!Number.isNaN(d.getTime())) {
      try {
        const date = d.toLocaleDateString("en-US", {
          timeZone: tz,
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        const time = d.toLocaleTimeString("en-US", {
          timeZone: tz,
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        });
        return `${date} · ${time}`;
      } catch {
        // Unknown timezone string: fall through to the stored local date.
      }
    }
  }
  if (row.event_local_date) {
    const [y, m, dd] = row.event_local_date.split("-").map(Number);
    if (y && m && dd) {
      return new Date(y, m - 1, dd).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  }
  return "—";
};

interface Props {
  eventType?: string;
  states?: string[];
}

const UpcomingEventsSection: React.FC<Props> = ({ eventType, states }) => {
  const [open, setOpen] = useState(true);
  const [days, setDays] = useState<number>(7);

  const { data, isLoading, error } = useQuery<UpcomingEventSummaryResult>({
    queryKey: [
      "event-executive-upcoming",
      days,
      eventType ?? null,
      states ?? null,
    ],
    queryFn: () => getUpcomingEventSummaries({ days, eventType, states }),
    staleTime: 60_000,
    enabled: open,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <section
      aria-labelledby="upcoming-events-heading"
      className="rounded-lg border border-gray-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2 text-left"
        >
          <span aria-hidden="true" className="text-gray-500">
            {open ? "▾" : "▸"}
          </span>
          <h2
            id="upcoming-events-heading"
            className="text-base font-semibold text-gray-900"
          >
            Upcoming
          </h2>
          {data && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
              {count.format(total)}
            </span>
          )}
        </button>
        {open && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-xs font-medium text-gray-600">
              Starting in the next
            </span>
            <select
              aria-label="Upcoming window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border border-gray-300 px-2 py-1"
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {open && (
        <div className="border-t border-gray-200">
          <p className="px-4 pt-3 text-xs text-gray-500">
            Events that have not started yet. Each one moves to the table below
            at midnight on its start day, in the event's own timezone. Uses the
            event type and state filters above.
          </p>
          {error ? (
            <div
              role="alert"
              className="m-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              {error instanceof Error
                ? error.message
                : "Unable to load upcoming events"}
            </div>
          ) : isLoading ? (
            <p className="px-4 py-4 text-sm text-gray-500">
              Loading upcoming events…
            </p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">
              No events starting in the next {days} days match these filters.
            </p>
          ) : (
            <>
              {/* Phone: one card per event. */}
              <ul className="divide-y divide-gray-100 md:hidden">
                {rows.map((row) => (
                  <li key={row.event_id} className="px-4 py-3 text-sm">
                    <Link
                      to={`/events/${row.event_id}`}
                      className="font-medium text-primary-700 hover:underline"
                    >
                      {row.event_title}
                    </Link>
                    <div className="mt-0.5 text-gray-700">
                      {formatEventStart(row)}
                    </div>
                    <div className="text-gray-500">
                      {[row.event_type, row.event_state, row.location_display]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Invited {count.format(row.invited_people_count)} ·
                      Registered{" "}
                      {count.format(
                        row.member_registrations + row.guest_registrations,
                      )}{" "}
                      · Vouchers {count.format(row.total_vouchers_minted)}
                    </div>
                  </li>
                ))}
              </ul>
              {/* Tablet/desktop: table. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-4 py-2">Event</th>
                      <th className="px-4 py-2">Starts (event time)</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">State</th>
                      <th className="px-4 py-2">Location</th>
                      <th className="px-4 py-2">Salespeople</th>
                      <th className="px-4 py-2 text-right">
                        Unique invited people
                      </th>
                      <th className="px-4 py-2 text-right">
                        Member registrations
                      </th>
                      <th className="px-4 py-2 text-right">
                        Guest registrations
                      </th>
                      <th className="px-4 py-2 text-right">Vouchers minted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr key={row.event_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <Link
                            to={`/events/${row.event_id}`}
                            className="font-medium text-primary-700 hover:underline"
                          >
                            {row.event_title}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2">
                          {formatEventStart(row)}
                        </td>
                        <td className="px-4 py-2">{row.event_type}</td>
                        <td className="px-4 py-2">{row.event_state || "—"}</td>
                        <td className="max-w-56 px-4 py-2">
                          {row.location_display || "—"}
                        </td>
                        <td className="max-w-56 px-4 py-2">
                          {row.salespeople || "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {count.format(row.invited_people_count)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {count.format(row.member_registrations)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {count.format(row.guest_registrations)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {count.format(row.total_vouchers_minted)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > rows.length && (
                <p className="px-4 py-2 text-xs text-gray-500">
                  Showing the first {rows.length} of {count.format(total)}.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default UpcomingEventsSection;
